import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Form, Row, Col } from 'react-bootstrap';

//components
import withPermissions from '~/hoc/withPermissions';
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';
import GenericList from '../../../components/GenericList/GenericList';
import Button from '../../../components/CustomButton/CustomButton';

//widgets
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget';

//assets
import CloseIcon from "~/assets/img/modal-close-icon.svg";
import DeleteIcon from '../../../assets/img/deleting.svg';

class BulkProjectAssignModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isManualAssign: false,
            selectedProjects: [],
            currentWidgetValue: null,
            reloadKey: Date.now(),
            page: 0,
            isPageAddedOrEdited: false,
        }
    }

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    }

    handleProjectChange = (value, fullObject) => {
        const { t, commonStore } = this.props;
        const { selectedProjects } = this.state;

        if (!value || !fullObject) return;
        const isDuplicate = selectedProjects.some(p => Number(p.value) === Number(fullObject.value));
        if (isDuplicate) {
            commonStore.addNotification(t('Project already selected'), null, 'warning');
            this.setState({ currentWidgetValue: null });
            return;
        }

        if (selectedProjects.length >= 100) {
            commonStore.addNotification(t('You can only select up to 100 projects'), null, 'error');
            this.setState({ currentWidgetValue: null });
            return;
        }

        this.setState(prevState => ({
            selectedProjects: [fullObject, ...prevState.selectedProjects],
            currentWidgetValue: null,
            reloadKey: Date.now(),
            page: 0,
            isPageAddedOrEdited: false
        }));
    }

    removeProject = (projectId) => {
        this.setState(prevState => ({
            selectedProjects: prevState.selectedProjects.filter(p => Number(p.value) !== Number(projectId)),
            reloadKey: Date.now(),
            isPageAddedOrEdited: true
        }));
    }

    fetchStaticRows = async (params = {}) => {
        const { selectedProjects } = this.state;
        const page = params.page || 0;
        const pageSize = params.pagesize || 6;

        const start = page * pageSize;
        const end = start + pageSize;
        const slicedData = selectedProjects.slice(start, end);

        return { rows: slicedData, pages: Math.ceil(selectedProjects.length / pageSize), total: selectedProjects.length };
    };

    handleSubmit = async () => {
        const { isManualAssign, selectedProjects } = this.state;
        const { userStore, commonStore, t, savedUserIds, onHide } = this.props;
        const data = {
            isManualAssign,
            projectIds: isManualAssign ? selectedProjects.map(p => Number(p.value)) : [],
            savedUserIds
        };
        const res = await userStore.bulkAssignProjectsToUsers(data);
        if(res.success) {
            commonStore.addNotification(t(res.message) || ('Saved'), null, 'success');
            onHide();
        } else {
            commonStore.addNotification(t(res.message) || ('Error while Assigning Projects.'), null, 'error');
        }
    }

    render() {
        const { t, show, onHide } = this.props;
        const { isManualAssign, selectedProjects, currentWidgetValue, reloadKey } = this.state;

        const isSubmitDisabled = (isManualAssign && selectedProjects.length === 0);

        const columns = [
            {
                Header: t('Project name'),
                id: 'name',
                accessor: 'name',
                className: 'text-left',
                Cell: ({ row }) => <div className="text-start">{row.original.label}</div>
            },
            {
                Header: t('Action'),
                id: 'action',
                accessor: 'id',
                className: 'text-center',
                maxWidth: 100,
                Cell: ({ row }) => (
                    <div className="d-flex align-items-center justify-content-center">
                        <Button icon_sm fill onClick={() => this.removeProject(row.original.value)} title={t('Remove')}>
                            <img src={DeleteIcon} alt="delete" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Modal size="lg" className="modal fade" show={show} onHide={onHide} backdrop="static" centered>
                <Modal.Header>
                    <Modal.Title>{t('Assign Projects to Members')}</Modal.Title>
                    <div className='modal-header__revised__closebtn' onClick={onHide}>
                        <img src={CloseIcon} className="cursor-pointer" alt='close_button' />
                    </div>
                </Modal.Header>

                <Modal.Body style={{ minHeight: '80vh' }}>
                    <div className="mb-2">
                        <Form.Check
                            type="radio"
                            id="mode-latest"
                            label={<strong>{t('Auto-assign latest 100 projects')}</strong>}
                            name="assignMode"
                            checked={!isManualAssign}
                            onChange={() => this.setState({ isManualAssign: false })}
                            className="mb-2"
                            style={{ cursor: 'pointer' }}
                        />
                        <div className="ms-4">
                            <i className="fa fa-info-circle me-2 text-muted"></i>
                            {t('This will automatically Assign the 100 most recently created projects to recent imported members.')}
                        </div>
                    </div>
                    <hr />
                    <div className="mb-3">
                        <Form.Check
                            type="radio"
                            id="mode-manual"
                            label={<strong>{t('Select projects manually')}</strong>}
                            name="assignMode"
                            checked={isManualAssign}
                            onChange={() => this.setState({ isManualAssign: true })}
                            className="mb-2"
                            style={{ cursor: 'pointer' }}
                        />

                        <div className='ms-4'>
                            <Row>
                                <Col md={12}>
                                    <label className="text-muted small mb-1">{t('Search')} & {t('Add Project')}</label>
                                    <ProjectsWidget
                                        className="form-control"
                                        onChange={(value, full) => this.handleProjectChange(value, full)}
                                        value={currentWidgetValue}
                                        disabled={!isManualAssign}
                                    />
                                    <small className="text-muted">
                                        {t('Selected items')}: {selectedProjects.length} / 100
                                    </small>
                                </Col>
                            </Row>

                            {selectedProjects.length > 0 && (
                                <GenericList
                                    key={reloadKey}
                                    columns={columns}
                                    requestData={(params) => this.fetchStaticRows(params)}
                                    page={this.state.page}
                                    isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                                    handlePageChange={this.handlePageChange}
                                    showPageSizeSelector
                                    pageSizeListId="bulk_project_assign"
                                />
                            )}
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={onHide}>
                        {t('Cancel')}
                    </Button>
                    <Button onClick={this.handleSubmit} disabled={isSubmitDisabled} wd fill>
                        {t('Assign Projects')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default inject('userStore', 'commonStore')(applicationRouter(withPermissions(withLocalization(observer(BulkProjectAssignModal)))));